<template>
  <div>
    <v-container grid-list-md>
      <v-layout row wrap>
        <v-flex xs12>
          <h1>{{ $t('security_pending_contracts') }}</h1>
          <p>Revisa y responde a las solicitudes de contrato que has recibido de empresas clientes.</p>
        </v-flex>
      </v-layout>

      <!-- Loading State -->
      <v-layout row wrap v-if="isLoading">
        <v-flex xs12>
          <v-progress-linear indeterminate></v-progress-linear>
          <p class="mt-3">{{ $t('loading') }}...</p>
        </v-flex>
      </v-layout>

      <!-- Error State -->
      <v-layout row wrap v-if="securityError">
        <v-flex xs12>
          <v-alert type="error" dismissible class="mb-3">
            {{ securityError }}
          </v-alert>
        </v-flex>
      </v-layout>

      <!-- Pending Contracts List -->
      <v-layout row wrap v-if="!isLoading && pendingContracts && pendingContracts.length > 0">
        <v-flex xs12>
          <v-card v-for="contract in pendingContracts" :key="contract.id" class="elevation-3 mb-4">
            <v-toolbar dark color="warning" class="mb-3">
              <v-toolbar-title>{{ contract.host_name }}</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-chip color="warning" text-color="white" small>
                {{ $t('security_pending') }}
              </v-chip>
            </v-toolbar>

            <v-card-text>
              <v-layout row wrap>
                <v-flex xs12 md6>
                  <h3 class="mb-3">{{ $t('security_company_info') }}</h3>
                  <v-list dense>
                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title>Empresa Cliente</v-list-tile-title>
                        <v-list-tile-sub-title>{{ contract.host_name }}</v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>

                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title>Proveedor de Seguridad</v-list-tile-title>
                        <v-list-tile-sub-title>{{ contract.security_name }}</v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>

                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title>{{ $t('created_at') }}</v-list-tile-title>
                        <v-list-tile-sub-title>{{ formatDate(contract.created_at) }}</v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>
                  </v-list>
                </v-flex>

                <v-flex xs12 md6>
                  <h3 class="mb-3">Acción Requerida</h3>
                  <p class="mb-4 grey--text">
                    {{ contract.host_name }} ha solicitado un contrato de seguridad con tu empresa.
                    Puedes aceptar o rechazar esta solicitud.
                  </p>
                  <v-layout row wrap>
                    <v-flex xs12 sm6 class="pr-2">
                      <v-btn
                        color="success"
                        block
                        :loading="respondingContractId === contract.id"
                        @click="respondToContract(contract.id, 'accept')"
                      >
                        <v-icon left>check_circle</v-icon>
                        Aceptar
                      </v-btn>
                    </v-flex>
                    <v-flex xs12 sm6 class="pl-2">
                      <v-btn
                        color="error"
                        block
                        :loading="respondingContractId === contract.id"
                        @click="respondToContract(contract.id, 'reject')"
                      >
                        <v-icon left>cancel</v-icon>
                        Rechazar
                      </v-btn>
                    </v-flex>
                  </v-layout>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- No Contracts Message -->
      <v-layout row wrap v-if="!isLoading && (!pendingContracts || pendingContracts.length === 0)">
        <v-flex xs12>
          <v-alert type="info" :value="true">
            No tienes contratos pendientes de aprobación en este momento.
          </v-alert>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import { FETCH_PENDING_CONTRACTS, RESPOND_TO_CONTRACT } from '../../../store/modules/security'
import { USER_REQUEST } from '../../../store/actions/user'
import { mapGetters } from 'vuex'

export default {
  name: 'SecurityPendingContracts',
  data() {
    return {
      respondingContractId: null
    }
  },
  computed: {
    ...mapGetters(['getToken']),
    ...mapGetters('security', [
      'isSecurityLoading',
      'getSecurityError',
      'getPendingContracts'
    ]),
    isLoading() {
      return this.isSecurityLoading
    },
    securityError() {
      return this.getSecurityError
    },
    pendingContracts() {
      return this.getPendingContracts
    }
  },
  methods: {
    fetchPendingContracts() {
      const token = this.getToken
      this.$store.dispatch(`security/${FETCH_PENDING_CONTRACTS}`, token)
    },
    respondToContract(contractId, action) {
      this.respondingContractId = contractId
      this.$store.dispatch(`security/${RESPOND_TO_CONTRACT}`, {
        token: this.getToken,
        contractId: contractId,
        action: action
      })
      .then(() => {
        const actionText = action === 'accept' ? 'aceptado' : 'rechazado'
        this.$store.commit('snackbar_change_message', `Contrato ${actionText} correctamente`)
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_activate_true')
        this.fetchPendingContracts()
      })
      .catch(err => {
        this.$store.commit('snackbar_change_message', 'Error al responder al contrato')
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
      })
      .finally(() => {
        this.respondingContractId = null
      })
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString(this.$i18n.locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
  },
  mounted() {
    if (!this.$store.getters.isAuthenticated) {
      this.$router.push('/')
      return
    }
    
    // Esperar a que el perfil esté cargado antes de cargar datos
    if (!this.$store.getters.isProfileLoaded) {
      this.$store.dispatch(USER_REQUEST)
        .then(() => {
          this.fetchPendingContracts()
        })
        .catch(() => {
          this.$router.push('/')
        })
    } else {
      this.fetchPendingContracts()
    }
  }
}
</script>

<style scoped>
.mb-4 {
  margin-bottom: 30px;
}
.mb-3 {
  margin-bottom: 20px;
}
.mt-3 {
  margin-top: 20px;
}
.pr-2 {
  padding-right: 10px;
}
.pl-2 {
  padding-left: 10px;
}
</style>
